import size from 'lodash/size';
import unzip from 'lodash/unzip';

import { createSelector } from 'reselect';

import {
  mean,
  medianSorted,
  linearRegression,
  sampleStandardDeviation as stdev,
  sampleCorrelation as correlation
} from 'simple-statistics';

import getParent from '../selectors';

import {
  getPrimaryParameterName, getPrimaryParameterType, getPrimaryParameterValues
} from 'store/scenes/bootstrapCIs/primaryParameter/selectors';

import { getFilteredParameterValues } from 'store/dataset';

import { getTestSelection } from '../testSelection';
import { getSelectedSampleId } from '../selectedSampleId';
import { getSecondaryParameterName } from '../secondaryParameter';
import { getPrimaryParameterLevel } from '../primaryParameterLevel';

const getSamples = state => getParent(state).samples;

export const getSamplesAmount = createSelector(getSamples, size);

export const getSelectedSample = createSelector(
  getSamples,
  getSelectedSampleId,

  (samples, selectedSampleId) => samples[selectedSampleId]
);

export const getSelectedSampleSummary = createSelector(
  getSelectedSample, s => ({
    mean: mean(s),
    stdev: s.length >= 2 ? stdev(s) : 'N/A',
    amount: s.length
  })
);

export const getOriginalSampleSummary = createSelector(
  getPrimaryParameterValues, v => ({
    mean: mean(v),
    stdev: v.length >= 2 ? stdev(v) : 'N/A',
    amount: v.length
  })
);

export const doSamplesExist = state =>
  getSamples(state).length !== 0;

const makeNumericalSelector = fn =>
  createSelector(getSamples,
    samples => samples.map(sample => {
      if (fn !== stdev) {
        return fn(sample);
      } else if (sample.length >= 2) {
        return fn(sample);
      } else {
        return null;
      }
    }));

export const getMeans = makeNumericalSelector(mean);
export const getStdevs = makeNumericalSelector(stdev);
export const getMedians = makeNumericalSelector(medianSorted);

export const getSlopes = makeNumericalSelector(
  sample => linearRegression(sample).m
);

export const getCorrelations = makeNumericalSelector(
  sample => sample.length >= 2 ? correlation(...unzip(sample)) : null
);

export const getProportions = createSelector(
  getSamples,
  getPrimaryParameterLevel,

  (samples, level) => samples.map(s =>
    s.filter(v => v.toString() === level).length / s.length
  )
);

export const getCounts = createSelector(
  getSamples,
  getPrimaryParameterLevel,

  (samples, level) => samples.map(s =>
    s.filter(v => v.toString() === level).length
  )
);

export const getSamplesSpecificationsTableData = state => {
  const parameterName = getPrimaryParameterName(state);
  const parameterLevel = getPrimaryParameterLevel(state);

  const bootstrappedSample = getSelectedSample(state);
  const parameterValues = getFilteredParameterValues(state, parameterName);

  const predicate = v => v.toString() === parameterLevel;

  return {
    totalAmount: {
      original: parameterValues.length,
      bootstrapped: bootstrappedSample.length
    },
    levelAmount: {
      original: parameterValues.filter(predicate).length,
      bootstrapped: bootstrappedSample.filter(predicate).length
    }
  };
};

export const getValues = state => {
  const secondaryParameterName = getSecondaryParameterName(state);
  const parameterType = getPrimaryParameterType(state);
  const testSelection = getTestSelection(state);

  if (parameterType === 'numerical') {
    if (secondaryParameterName) {
      switch (testSelection) {
        case 'slope':
          return getSlopes(state);
        case 'correlation':
          return getCorrelations(state);
        default:
          return null;
      }
    }

    switch (testSelection) {
      case 'mean':
        return getMeans(state);
      case 'median':
        return getMedians(state);
      case 'stdev':
        return getStdevs(state);
      default:
        return null;
    }
  }

  if (parameterType === 'categorical') {
    switch (testSelection) {
      case 'proportion':
        return getProportions(state);
      case 'count':
        return getCounts(state);
      default:
        return null;
    }
  }

  return null;
};

export const getSamplesMean = createSelector(getValues, mean);
export const getSamplesStdev = createSelector(getValues, stdev);

export default getSamples;
